import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface SearchableSelectOption {
  value: string;
  label: string;
  searchText?: string;
}

@Component({
  selector: 'app-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true,
    },
  ],
})
export class SearchableSelectComponent implements ControlValueAccessor, OnChanges {
  @Input() options: SearchableSelectOption[] = [];
  @Input() placeholder = '';
  @Input() searchPlaceholder = '';
  @Input() allowEmpty = true;
  @Input() emptyLabel = '';
  @Input() disabled = false;
  @Output() selectionChange = new EventEmitter<string>();

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('triggerBtn') triggerBtn?: ElementRef<HTMLButtonElement>;

  open = false;
  query = '';
  value = '';
  filteredOptions: SearchableSelectOption[] = [];
  panelStyle: Record<string, string> = {};

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private host: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.applyFilter();
      // Keep selected label in sync when options arrive asynchronously (e.g. areas).
      if (this.value && !this.options.some(option => option.value === this.value)) {
        // Value may still be valid while options are loading; don't clear it.
      }
    }
  }

  get displayLabel(): string {
    if (!this.value) {
      return '';
    }
    return this.options.find(option => option.value === this.value)?.label || this.value;
  }

  writeValue(value: string | null): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  toggle(event?: Event): void {
    event?.stopPropagation();
    if (this.disabled) {
      return;
    }
    this.open = !this.open;
    if (this.open) {
      this.query = '';
      this.applyFilter();
      this.updatePanelPosition();
      setTimeout(() => this.searchInput?.nativeElement.focus(), 0);
    } else {
      this.onTouched();
    }
  }

  select(option: SearchableSelectOption | null, event?: Event): void {
    event?.stopPropagation();
    const next = option?.value ?? '';
    this.value = next;
    this.onChange(next);
    this.onTouched();
    this.selectionChange.emit(next);
    this.open = false;
    this.query = '';
  }

  onQueryChange(): void {
    this.applyFilter();
  }

  trackByValue(_: number, option: SearchableSelectOption): string {
    return option.value;
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.open) {
      return;
    }
    const target = event.target as Node | null;
    if (target && !this.host.nativeElement.contains(target)) {
      // Fixed panel is still inside host element tree
      this.open = false;
      this.onTouched();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open) {
      this.open = false;
      this.onTouched();
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.open) {
      this.updatePanelPosition();
    }
  }

  private updatePanelPosition(): void {
    const trigger = this.triggerBtn?.nativeElement || this.host.nativeElement;
    const rect = trigger.getBoundingClientRect();
    const panelMaxHeight = 288;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < panelMaxHeight && rect.top > spaceBelow;
    const width = Math.max(rect.width, 220);

    this.panelStyle = {
      position: 'fixed',
      left: `${Math.min(rect.left, window.innerWidth - width - 8)}px`,
      width: `${width}px`,
      zIndex: '2000',
      ...(openUp
        ? { bottom: `${window.innerHeight - rect.top + 6}px`, top: 'auto' }
        : { top: `${rect.bottom + 6}px`, bottom: 'auto' }),
    };
  }

  private applyFilter(): void {
    const term = this.query.trim().toLowerCase();
    const source = this.options || [];
    this.filteredOptions = !term
      ? [...source]
      : source.filter(option => {
          const haystack = `${option.label} ${option.searchText || ''} ${option.value}`.toLowerCase();
          return haystack.includes(term);
        });
  }
}
